import * as React from 'react';
import {
    ICommand,
    connectToItemCache,
    IItemCache,
    Action,
    IActionProps,
    withSelectedItems,
    withExactlyOneItem,
} from '@ms/items-view';
import { ControlIcons } from 'owa-control-icons';
import { fileKeyFacet } from '../Schema/Facets';
import { getParameterStore } from '../store/ParametersStore';
import { getFilterStore } from '../store/FilterStore';
import { getFilterMenuStore } from '../store/FilterMenuStore';
import { DateRangeOption } from 'owa-files-filter-date-filter';
import { FileExtensionType } from 'owa-files-filter-file-extensions-filter';
import { AttachmentModel } from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import {
    print,
    isPrintAllowed,
    download,
    isDownloadAllowed,
    openInNewTab,
} from 'owa-attachment-user-operations';
import LinkAttachment from 'owa-service/lib/contract/LinkAttachment';

const DownloadSelectedItemsAction = withSelectedItems()(
    withExactlyOneItem()(
        connectToItemCache(
            (
                itemCache: IItemCache,
                props: { itemKey: string }
            ): Pick<IActionProps, 'isAvailable' | 'onExecute'> => {
                const { itemKey } = props;

                let isAvailable = false;

                let attachmentId;
                let attachment: AttachmentModel;

                const parameterStore = getParameterStore();

                if (itemKey) {
                    attachmentId = {
                        Id: itemCache.demandItemFacet(fileKeyFacet, itemKey).id,
                        mailboxInfo: parameterStore.mailboxInfo,
                    };
                    attachment = getAttachment(attachmentId);
                    isAvailable = isDownloadAllowed(attachment.model);
                }

                return {
                    isAvailable: !!isAvailable,
                    onExecute: isAvailable
                        ? async () => {
                              download(attachmentId, attachment.model);
                          }
                        : undefined,
                };
            }
        )(Action)
    )
);

const PrintSelectedItemsAction = withSelectedItems()(
    withExactlyOneItem()(
        connectToItemCache(
            (
                itemCache: IItemCache,
                props: { itemKey: string }
            ): Pick<IActionProps, 'isAvailable' | 'onExecute'> => {
                const { itemKey } = props;

                let isAvailable = false;

                let attachmentId;
                let attachment: AttachmentModel;

                const parameterStore = getParameterStore();

                if (itemKey) {
                    attachmentId = {
                        Id: itemCache.demandItemFacet(fileKeyFacet, itemKey).id,
                        mailboxInfo: parameterStore.mailboxInfo,
                    };
                    attachment = getAttachment(attachmentId);
                    isAvailable = isPrintAllowed(attachment.model);
                }

                return {
                    isAvailable: !!isAvailable,
                    onExecute: isAvailable
                        ? async () => {
                              print(attachmentId, attachment.model);
                          }
                        : undefined,
                };
            }
        )(Action)
    )
);

const OpenInNewTabSelectedItemAction = withSelectedItems()(
    withExactlyOneItem()(
        connectToItemCache(
            (
                itemCache: IItemCache,
                props: { itemKey: string }
            ): Pick<IActionProps, 'isAvailable' | 'onExecute'> => {
                const { itemKey } = props;

                let attachmentId;
                let attachment: AttachmentModel;
                let webUrl;

                const parameterStore = getParameterStore();

                if (itemKey) {
                    attachmentId = {
                        Id: itemCache.demandItemFacet(fileKeyFacet, itemKey).id,
                        mailboxInfo: parameterStore.mailboxInfo,
                    };
                    attachment = getAttachment(attachmentId);
                    webUrl = (attachment.model as LinkAttachment).WebUrl;
                }

                return {
                    isAvailable: !!webUrl,
                    onExecute: async () => {
                        openInNewTab(webUrl);
                    },
                };
            }
        )(Action)
    )
);

export const downloadCommand: ICommand = {
    key: 'download',
    name: 'Download', //TODO localize
    iconProps: { iconName: ControlIcons.Download },
    action: <DownloadSelectedItemsAction />,
};

export const printCommand: ICommand = {
    key: 'print',
    name: 'Print', //TODO localize
    iconProps: { iconName: ControlIcons.Print },
    action: <PrintSelectedItemsAction />,
};

export const openInNewTabCommand: ICommand = {
    key: 'openInNewTab',
    name: 'Open in new tab',
    iconProps: { iconName: ControlIcons.OpenInNewWindow },
    action: <OpenInNewTabSelectedItemAction />,
};

export function isDateRangeOptionSelectedCheck(value: DateRangeOption): boolean {
    return getFilterStore().dateRangeFilter.selectedValue.option === value;
}

export function isExtensionSelected(value: FileExtensionType): boolean {
    return getFilterStore().extensionGroupFilter.selectedValue.has(value.toString());
}

export function isExtensionFilterExpanded(): boolean {
    return getFilterMenuStore().isExtensionFilterExpanded;
}

export function getContextMenuActions() {
    return [downloadCommand, printCommand];
}
